import React, { useMemo, useState, useCallback } from 'react';
import StickyScrollTable from '../../../../components/common/StickyScrollTable';
import PreferencesDataTable from '../../../../components/common/PreferencesDataTable';

export default function VehicleWarrantyInformationTable({ items, totalCount, loading, lazyParams, onPage, columns }) {
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(0);

    const normalized = useMemo(() => (Array.isArray(items) ? items : []), [items]);

    const sortedData = useMemo(() => {
        if (!sortField || sortOrder === 0) return normalized;

        const isDateKey = (key) => /date|tarih|baslangic|bitis/i.test(key);
        
        return [...normalized].sort((a, b) => {
            let aVal = a?.[sortField];
            let bVal = b?.[sortField];

            if (aVal == null && bVal == null) return 0;
            if (aVal == null) return sortOrder;
            if (bVal == null) return -sortOrder;

            const looksLikeDate = isDateKey(sortField) || 
                (typeof aVal === 'string' && /^\d{2}\.\d{2}\.\d{4}/.test(aVal)) || 
                aVal instanceof Date || bVal instanceof Date;

            if (looksLikeDate) {
                const parseDate = (val) => {
                    if (val instanceof Date) return val;
                    if (typeof val === 'string' && /^\d{2}\.\d{2}\.\d{4}/.test(val)) {
                        const [day, month, year] = val.split('.');
                        return new Date(year, month - 1, day);
                    }
                    return new Date(val);
                };
                const da = parseDate(aVal);
                const db = parseDate(bVal);
                return sortOrder * (da.getTime() - db.getTime());
            }

            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return sortOrder * (aVal - bVal);
            }

            return sortOrder * String(aVal).localeCompare(String(bVal), 'tr', { 
                numeric: true, 
                sensitivity: 'base' 
            });
        });
    }, [normalized, sortField, sortOrder]);

    const handleSort = useCallback((e) => {
        setSortField(e.sortField);
        setSortOrder(e.sortOrder);
    }, []);

    const label = (k) =>
        k
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, (s) => s.toUpperCase())
            .trim();

    const dateFields = useMemo(() => {
        const set = new Set();
        (columns || []).forEach((c) => {
            if (c?.dataType === 'date' || /date|tarih|baslangic|bitis/i.test(c?.field)) {
                set.add(c.field);
            }
        });
        return set;
    }, [columns]);

    const formatDate = (val) => {
        if (!val) return '';
        if (typeof val === 'string' && /^\d{2}\.\d{2}\.\d{4}/.test(val)) {
            return val;
        }
        const d = typeof val === 'string' ? new Date(val) : val instanceof Date ? val : null;
        if (!d || isNaN(d.getTime())) return '';
        return d.toLocaleDateString('tr-TR');
    };

    const cell = (row, key) => {
        const v = row?.[key];
        if (v == null) return '';
        if (dateFields.has(key)) return formatDate(v);
        if (typeof v === 'number') return new Intl.NumberFormat('tr-TR').format(v);
        return String(v);
    };

    const prefColumns = useMemo(
        () =>
            (columns || []).map((c) => ({
                field: c.field,
                columnKey: c.field,
                header: c.header ?? label(c.field),
                sortable: true,
                body: c.body ? (row) => c.body(row) : (row) => cell(row, c.field)
            })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [columns, dateFields]
    );

    return (
        <StickyScrollTable deps={[sortedData, columns]} scrollBarPosition="top">
            <PreferencesDataTable
                tableKey="vehicle-warranty-information-report"
                columns={prefColumns}
                value={sortedData}
                dataKey="SasiNo"
                lazy
                paginator
                first={lazyParams.first || 0}
                rows={lazyParams.rows || 10}
                totalRecords={totalCount}
                onPage={onPage}
                loading={loading}
                onSort={handleSort}
                sortField={sortField || undefined}
                sortOrder={sortOrder || 0}
                sortMode="single"
                rowsPerPageOptions={[10, 25, 50, 100, 200, 500]}
                showGridlines
                className="p-datatable-gridlines"
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                emptyMessage="Kayıt bulunamadı."
                tableStyle={{ minWidth: '50rem' }}
                pt={{ wrapper: { style: { overflowX: 'scroll' } } }}
            />
        </StickyScrollTable>
    );
}